import { Component, inject, computed, OnInit, signal, AfterViewInit, OnDestroy, ViewChild, ElementRef, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PagesModule } from '@gravity-angular/pages';
import { ComponentsModule } from '@gravity-angular/components';
import { BaseModule } from '@gravity-angular/base';
import { NgxGaugeModule } from 'ngx-gauge';
import { DashboardDataService } from '../../core/services/dashboard-data.service';
import { DashboardStateService } from '../../core/services/dashboard-state.service';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { ERROR_CODES, WARNING_CODES, ERROR_COLOR_MAP, WARNING_COLOR_MAP } from '../../shared/utils/error-warning-constants';
import { EVG_COLORS, PARAMETER_CONFIG, CHART_ANIMATION, CHART_STYLE, REFERENCE_BANDS } from '../../shared/utils/chart-config.constants';
import { analyzeEVGData } from '../../shared/utils/error-warning-utils';

// Register Chart.js components
Chart.register(...registerables);

@Component({
  selector: 'app-live-data',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PagesModule,
    ComponentsModule,
    BaseModule,
    NgxGaugeModule
  ],
  templateUrl: './live-data.html',
  styleUrl: './live-data.scss',
})
export class LiveData implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('evgChart', { static: false }) chartCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('liveTooltipEl', { static: false }) private liveTooltipEl?: ElementRef<HTMLDivElement>;
  
  // Make Object available in template
  Object = Object;
  
  dashboardData = inject(DashboardDataService);
  state = inject(DashboardStateService);
  
  private chart?: Chart;
  private errorAnnotations: Array<{x: number; label: string; color: string}> = [];
  private hoveredX: number | null = null;

  // Tooltip signals
  tooltipVisible = signal(false);
  tooltipLeft = signal(0);
  tooltipTop = signal(0);
  tooltipWidth = signal(200);
  tooltipTime = signal('');
  tooltipEvgCards = signal<Array<{
    evgId: number;
    color: string;
    voltage: string | null;
    current: string | null;
    pwm: string | null;
    temp: string | null;
    errors: Array<{ code: number; label: string; badgeColor: string }>;
    warnings: Array<{ code: number; label: string; badgeColor: string }>;
    trendDir: 'up' | 'down' | 'stable';
    trendArrow: string;
  }>>([]);

  statsBar = signal<Array<{
    param: string; label: string; suffix: string;
    min: string; max: string; avg: string;
  }>>([]);

  private readonly annotationPlugin = {
    id: 'errorAnnotations',
    afterDraw: (chart: any) => {
      const anns = this.errorAnnotations;
      const ctx = chart.ctx as CanvasRenderingContext2D;
      const xScale = chart.scales['x'];
      if (!xScale) return;

      const MARKER_R = 5;
      const MARKER_H = 9;
      const TOP    = chart.chartArea.top;
      const BOTTOM = chart.chartArea.bottom;

      // --- Hover crosshair line ---
      if (this.hoveredX !== null) {
        const hPx = xScale.getPixelForValue(this.hoveredX);
        if (hPx >= xScale.left && hPx <= xScale.right) {
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(hPx, TOP);
          ctx.lineTo(hPx, BOTTOM);
          ctx.lineWidth = 1;
          ctx.strokeStyle = 'rgba(148,163,184,0.6)';
          ctx.setLineDash([4, 3]);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        }
      }

      if (!anns.length) return;

      // Deduplicate by pixel position
      const byX = new Map<number, { errorColor: string | null; warningColor: string | null }>();
      anns.forEach(ann => {
        const xPx = Math.round(xScale.getPixelForValue(ann.x));
        if (xPx < xScale.left || xPx > xScale.right) return;
        let key = xPx;
        for (const k of byX.keys()) {
          if (Math.abs(k - xPx) <= 4) { key = k; break; }
        }
        if (!byX.has(key)) byX.set(key, { errorColor: null, warningColor: null });
        const entry = byX.get(key)!;
        if (ann.label.startsWith('E')) entry.errorColor = ann.color;
        else entry.warningColor = ann.color;
      });

      byX.forEach(({ errorColor, warningColor }, xPx) => {
        ctx.save();
        // Error → filled circle at BOTTOM edge
        if (errorColor) {
          ctx.beginPath();
          ctx.arc(xPx, BOTTOM + MARKER_R + 3, MARKER_R, 0, Math.PI * 2);
          ctx.fillStyle = errorColor;
          ctx.fill();
        }
        // Warning → downward-pointing triangle at TOP edge (tip pointing into chart)
        if (warningColor) {
          const tipY  = TOP + 2;              // tip points down into chart area
          const baseY = tipY - MARKER_H;      // base sits above the chart
          ctx.beginPath();
          ctx.moveTo(xPx, tipY);
          ctx.lineTo(xPx - MARKER_H * 0.65, baseY);
          ctx.lineTo(xPx + MARKER_H * 0.65, baseY);
          ctx.closePath();
          ctx.fillStyle = warningColor;
          ctx.fill();
        }
        ctx.restore();
      });
    }
  };

  private readonly referenceBandPlugin = {
    id: 'referenceBandsLive',
    beforeDatasetsDraw: (chart: any) => {
      const { ctx, chartArea, scales } = chart;
      if (!chartArea) return;
      REFERENCE_BANDS.forEach(({ axisId, lo, hi, color }) => {
        const scale = scales[axisId];
        if (!scale) return;
        const pxHi = Math.min(scale.getPixelForValue(hi), scale.getPixelForValue(lo));
        const pxLo = Math.max(scale.getPixelForValue(hi), scale.getPixelForValue(lo));
        const top  = Math.max(pxHi, chartArea.top);
        const bot  = Math.min(pxLo, chartArea.bottom);
        if (bot <= top) return;
        ctx.save();
        ctx.fillStyle = color;
        ctx.fillRect(chartArea.left, top, chartArea.width, bot - top);
        ctx.restore();
      });
    }
  };

  // Get current data
  currentData = this.dashboardData.currentData;
  countdown = this.dashboardData.getCountdownFormatted;
  lastUpdateFormatted = this.dashboardData.getLastUpdateFormatted;
  connectionStatus = this.dashboardData.connectionStatus;
  systemState = this.dashboardData.getSystemState;
  systemVitals = this.dashboardData.getSystemVitals;
  uvSensors = this.dashboardData.getUVSensors;
  historicalData = this.dashboardData.getHistoricalData;
  isDeviceOnline = this.dashboardData.isDeviceOnline;
  deviceLastSeen = this.dashboardData.getDeviceLastSeenFormatted;
  isOffline = computed(() => !this.isDeviceOnline());

  // Rolling window sizes in milliseconds for the scrolling time axis
  readonly WINDOW_MS: Record<string, number> = {
    '1m': 60_000, '5m': 300_000, '15m': 900_000, '30m': 1_800_000, '1h': 3_600_000
  };
  liveTimeRange = signal<'1m' | '5m' | '15m' | '30m' | '1h'>('5m');

  private chartTicker?: ReturnType<typeof setInterval>;

  // Operational % based on active EVG count
  operationalPercentage = computed(() => {
    const data = this.currentData();
    if (!data?.devices?.length) return 0;
    let active = 0;
    for (let i = 1; i <= 6; i++) {
      const evg = this.dashboardData.extractEVGData(data.devices[0], i);
      if (evg?.running) active++;
    }
    return Math.round((active / 6) * 100);
  });

  // EVG numbers array
  evgNumbers = [1, 2, 3, 4, 5, 6] as const;
  
  // Chart selection state — default to Voltage for all EVGs (6 clean lines on load)
  selectedSeries = signal<Array<{evgId: number, parameter: 'voltage' | 'current' | 'pwm' | 'temp'}>>(
    [1, 2, 3, 4, 5, 6].map(evgId => ({ evgId, parameter: 'voltage' as const }))
  );
  
  // Card collapse state — racks open by default for immediate visibility
  racksExpanded = signal<boolean>(true);
  sensorsExpanded = signal<boolean>(false);
  
  // Error and Warning Codes
  errorCodes = ERROR_CODES;
  warningCodes = WARNING_CODES;
  selectedErrorCodes = signal<number[]>(Object.keys(ERROR_CODES).map(Number));
  selectedWarningCodes = signal<number[]>(Object.keys(WARNING_CODES).map(Number));
  showErrorIndicators = signal<boolean>(true);
  showWarningIndicators = signal<boolean>(true);
  errorCodesExpanded = signal<boolean>(false);
  warningCodesExpanded = signal<boolean>(false);
  
  constructor() {
    // Update chart when selection, time range, or annotation toggles change
    effect(() => {
      const series = this.selectedSeries();
      const history = this.historicalData();
      const range = this.liveTimeRange();
      const _e = this.showErrorIndicators();
      const _w = this.showWarningIndicators();
      const _ec = this.selectedErrorCodes();
      const _wc = this.selectedWarningCodes();
      this.updateChart();
    });
  }
  
  ngOnInit(): void {
    // Data polling starts automatically in service
  }
  
  ngAfterViewInit(): void {
    // Initialize chart after view is ready
    setTimeout(() => {
      this.initializeChart();
      // Advance the time window every second so the axis scrolls even with no data
      this.chartTicker = setInterval(() => this.tickChart(), 1000);
      // Portal tooltip to document.body to escape xg-sidenav's CSS transform containing block
      if (this.liveTooltipEl?.nativeElement) {
        document.body.appendChild(this.liveTooltipEl.nativeElement);
      }
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.chartTicker) clearInterval(this.chartTicker);
    if (this.chart) this.chart.destroy();
    this.liveTooltipEl?.nativeElement?.remove();
  }
  
  /**
   * Manual refresh handler
   */
  async onRefresh(): Promise<void> {
    await this.dashboardData.manualRefresh();
  }
  
  /**
   * Get EVG data for display
   */
  getEVGData(evgNum: number) {
    const data = this.currentData();
    if (!data?.devices || data.devices.length === 0) return null;
    
    // Extract EVG data from first device
    return this.dashboardData.extractEVGData(data.devices[0], evgNum);
  }
  
  /**
   * Get count of active EVG units
   */
  getActiveEVGCount(): number {
    let count = 0;
    for (let i = 1; i <= 6; i++) {
      const evg = this.getEVGData(i);
      if (evg?.running) count++;
    }
    return count;
  }
  
  /**
   * Deselect all EVGs
   */
  deselectAllEVGs(): void {
    this.selectedSeries.set([]);
  }

  /**
   * Select all 24 series (all params for all EVGs)
   */
  selectAllEVGs(): void {
    this.selectedSeries.set(
      [1, 2, 3, 4, 5, 6].flatMap(evgId =>
        (['voltage', 'current', 'pwm', 'temp'] as const).map(parameter => ({ evgId, parameter }))
      )
    );
  }

  /**
   * Quick-select: show one parameter across all 6 EVGs
   */
  selectByParameter(param: 'voltage' | 'current' | 'pwm' | 'temp'): void {
    this.selectedSeries.set(
      [1, 2, 3, 4, 5, 6].map(evgId => ({ evgId, parameter: param }))
    );
  }

  /**
   * Toggle all 4 parameters for one EVG at once
   */
  toggleEVG(evgId: number): void {
    const params = ['voltage', 'current', 'pwm', 'temp'] as const;
    const current = this.selectedSeries();
    const allSelected = params.every(p => current.some(s => s.evgId === evgId && s.parameter === p));
    const without = current.filter(s => s.evgId !== evgId);
    if (allSelected) {
      this.selectedSeries.set(without);
    } else {
      this.selectedSeries.set([...without, ...params.map(p => ({ evgId, parameter: p }))]);
    }
  }

  /**
   * Returns selection state for one EVG: 'all' | 'partial' | 'none'
   */
  isEVGSelected(evgId: number): 'all' | 'partial' | 'none' {
    const params = ['voltage', 'current', 'pwm', 'temp'] as const;
    const count = params.filter(p => this.selectedSeries().some(s => s.evgId === evgId && s.parameter === p)).length;
    if (count === 4) return 'all';
    if (count > 0) return 'partial';
    return 'none';
  }

  /**
   * Toggle individual parameter for specific EVG
   */
  toggleParameter(evgId: number, parameter: 'voltage' | 'current' | 'pwm' | 'temp'): void {
    const current = this.selectedSeries();
    const exists = current.find(s => s.evgId === evgId && s.parameter === parameter);
    
    if (exists) {
      // Remove this series
      this.selectedSeries.set(current.filter(s => !(s.evgId === evgId && s.parameter === parameter)));
    } else {
      // Add this series
      this.selectedSeries.set([...current, { evgId, parameter }]);
    }
  }
  
  /**
   * Check if parameter is selected for EVG
   */
  isParameterSelected(evgId: number, parameter: 'voltage' | 'current' | 'pwm' | 'temp'): boolean {
    return this.selectedSeries().some(s => s.evgId === evgId && s.parameter === parameter);
  }
  
  /**
   * Get count of selected series
   */
  getSelectedSeriesCount(): number {
    return this.selectedSeries().length;
  }
  
  /**
   * Toggle error code selection
   */
  toggleErrorCode(code: number): void {
    const current = this.selectedErrorCodes();
    if (current.includes(code)) {
      this.selectedErrorCodes.set(current.filter(c => c !== code));
    } else {
      this.selectedErrorCodes.set([...current, code]);
    }
  }
  
  /**
   * Toggle warning code selection
   */
  toggleWarningCode(code: number): void {
    const current = this.selectedWarningCodes();
    if (current.includes(code)) {
      this.selectedWarningCodes.set(current.filter(c => c !== code));
    } else {
      this.selectedWarningCodes.set([...current, code]);
    }
  }
  
  /**
   * Select all error codes
   */
  selectAllErrors(): void {
    this.selectedErrorCodes.set(Object.keys(ERROR_CODES).map(Number));
  }
  
  /**
   * Deselect all error codes
   */
  deselectAllErrors(): void {
    this.selectedErrorCodes.set([]);
  }
  
  /**
   * Select all warning codes
   */
  selectAllWarnings(): void {
    this.selectedWarningCodes.set(Object.keys(WARNING_CODES).map(Number));
  }
  
  /**
   * Deselect all warning codes
   */
  deselectAllWarnings(): void {
    this.selectedWarningCodes.set([]);
  }
  
  /**
   * Get error code description
   */
  getErrorDescription(code: number): string {
    const descriptions: Record<number, string> = {
      120: 'In Ignition cycle if both lamps not ON then lamp goes to off state. After the Igntion cycle (5 cycles) if still lamp are not ON, then Lamp goes to OFF state. After the Reignition time is over, it will check any one lamp is ON, if not it will go to LAMP OFF state.',
      130: 'Communication error with LMS BUS. Check wiring and connections.',
      140: 'UV intensity too low. Check lamp condition and power settings.',
      150: 'UV intensity too high. Check sensor calibration.',
      160: 'EVG temperature exceeds safe operating limits. Check cooling system.',
      170: 'Power supply failure detected. Check power connections.',
      180: 'Lamp current overflow. Reduce power level or check lamp condition.',
      190: 'Critical lamp current levels. Immediate attention required.'
    };
    return descriptions[code] || 'No description available.';
  }
  
  /**
   * Get warning code description
   */
  getWarningDescription(code: number): string {
    const descriptions: Record<number, string> = {
      210: 'UV intensity is dropping. Monitor lamp condition.',
      220: 'Power supply voltage is low. Check power source.',
      230: 'Lamp re-ignition cycle triggered. Monitor lamp stability.',
      240: 'Lamp 1 showing degraded performance. Consider replacement.',
      250: 'Lamp 2 showing degraded performance. Consider replacement.'
    };
    return descriptions[code] || 'No description available.';
  }
  
  /**
   * Get color for error code
   */
  getErrorColor(code: number): string {
    return ERROR_COLOR_MAP[code] || '#ef4444';
  }
  
  /**
   * Get color for warning code
   */
  getWarningColor(code: number): string {
    return WARNING_COLOR_MAP[code] || '#f59e0b';
  }
  
  /**
   * Set live chart time range
   */
  setLiveTimeRange(range: '1m' | '5m' | '15m' | '30m' | '1h'): void {
    this.liveTimeRange.set(range);
  }

  /**
   * Toggle racks section
   */
  toggleRacks(): void {
    this.racksExpanded.update(v => !v);
  }
  
  /**
   * Toggle sensors section
   */
  toggleSensors(): void {
    this.sensorsExpanded.update(v => !v);
  }
  
  /**
   * UI Helper Methods
   */
  getSystemStateBadgeType(state: string): 'success' | 'warn' | 'error' | 'neutral' {
    const badgeMap: Record<string, 'success' | 'warn' | 'error' | 'neutral'> = {
      'System Running': 'success',
      'Warning': 'warn',
      'Alarm': 'error',
      'Shutdown': 'error'
    };
    return badgeMap[state] || 'neutral';
  }
  
  getEVGStatusBadgeType(evgData: any): 'success' | 'neutral' {
    return (evgData?.lamp1_status || evgData?.lamp2_status) ? 'success' : 'neutral';
  }
  
  /**
   * Get EVG identity color
   */
  getEVGColor(evgNum: number): string {
    return EVG_COLORS[evgNum - 1] || '#6b7280';
  }

  /**
   * Get temperature Tailwind text color class
   */
  getTempClass(temp: number): string {
    if (!temp || temp <= 0) return 'text-gray-400';
    if (temp < 40) return 'text-green-600';
    if (temp < 60) return 'text-yellow-600';
    return 'text-red-600 font-bold';
  }

  /**
   * Get clamped PWM width for progress bar (0–100)
   */
  getPWMWidth(pwm: number): number {
    return Math.min(100, Math.max(0, pwm));
  }

  getConnectionBadgeType(): 'success' | 'error' | 'warn' {
    const statusMap: Record<string, 'success' | 'error' | 'warn'> = {
      'connected': 'success',
      'error': 'error'
    };
    return statusMap[this.connectionStatus()] || 'warn';
  }
  
  /**
   * Initialize Chart.js chart with a linear time axis (millisecond x values)
   */
  private initializeChart(): void {
    if (!this.chartCanvas) return;
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    const now = Date.now();
    const windowMs = this.WINDOW_MS[this.liveTimeRange()] ?? 300_000;

    const config: ChartConfiguration = {
      type: 'line',
      data: { datasets: [] },
      plugins: [this.annotationPlugin, this.referenceBandPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,           // disable per-update animation for smooth scrolling
        layout: { padding: { top: 14, bottom: 18 } }, // top: warning triangles, bottom: error circles
        plugins: {
          legend: { display: true, position: 'top' },
          title: { display: false },
          tooltip: {
            enabled: false,
            external: (context: any) => {
              const { chart, tooltip } = context;
              if (tooltip.opacity === 0) {
                this.hoveredX = null;
                this.tooltipVisible.set(false);
                chart.draw();
                return;
              }

              const xVal: number = tooltip.dataPoints?.[0]?.parsed?.x;
              if (xVal == null) return;
              this.hoveredX = xVal;

              // Time label
              this.tooltipTime.set(
                new Date(xVal).toLocaleString('en-US', {
                  month: 'short', day: 'numeric',
                  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                })
              );

              // Group dataPoints by EVG ID
              type ParamKey = 'voltage' | 'current' | 'pwm' | 'temp';
              const byEvg = new Map<number, Partial<Record<ParamKey, string>>>();
              (tooltip.dataPoints ?? []).forEach((dp: any) => {
                const m = (dp.dataset.label as string).match(/^EVG (\d+) - (.+)/);
                if (!m) return;
                const evgId = Number(m[1]);
                const paramKey = this.getLabelToParam(m[2]);
                if (!paramKey) return;
                if (!byEvg.has(evgId)) byEvg.set(evgId, {});
                byEvg.get(evgId)![paramKey] = dp.parsed.y?.toFixed(2) ?? '-';
              });

              // Compute trend: compare current voltage vs value >30s ago
              const trendByEvg = new Map<number, 'up' | 'down' | 'stable'>();
              const histSnaps = this.historicalData();
              [...new Set(this.selectedSeries().map((s: any) => s.evgId as number))].forEach(evgId => {
                const prevSnaps = histSnaps.filter(h =>
                  h.devices?.length && (h.timestamp as Date).getTime() < xVal - 30_000
                );
                if (!prevSnaps.length) return;
                const prev = this.dashboardData.extractEVGData(prevSnaps[prevSnaps.length - 1].devices[0], evgId);
                if (!prev) return;
                const curVals = byEvg.get(evgId) ?? {};
                const prevV = prev.voltage;
                const curV = parseFloat(curVals.voltage ?? String(prevV));
                const pct = prevV ? (curV - prevV) / prevV * 100 : 0;
                trendByEvg.set(evgId, pct > 1 ? 'up' : pct < -1 ? 'down' : 'stable');
              });

              // Build per-EVG cards (only for EVGs that have data at this cursor position)
              const selectedEvgIds = [...new Set(this.selectedSeries().map((s: any) => s.evgId as number))]
                .filter(id => byEvg.has(id));
              const WINDOW = 2000;
              const cards = selectedEvgIds.map(evgId => {
                const vals = byEvg.get(evgId) ?? {};
                const errMap = new Map<number, string>();
                const warnMap = new Map<number, string>();
                this.errorAnnotations.forEach(ann => {
                  if (Math.abs(ann.x - xVal) > WINDOW) return;
                  const typeChar = ann.label[0];
                  const rest = ann.label.slice(1);
                  const [codeStr, evgStr] = rest.split('·');
                  if (Number(evgStr) !== evgId) return;
                  const code = Number(codeStr);
                  if (typeChar === 'E') errMap.set(code, ann.color);
                  else warnMap.set(code, ann.color);
                });
                const errs = [...errMap.entries()].map(([code, badgeColor]) => ({ code, label: `E${code}`, badgeColor }));
                const warns = [...warnMap.entries()].map(([code, badgeColor]) => ({ code, label: `W${code}`, badgeColor }));
                const tDir = trendByEvg.get(evgId) ?? 'stable';
                return {
                  evgId,
                  color: EVG_COLORS[evgId - 1] ?? '#6b7280',
                  voltage: vals.voltage ?? null,
                  current: vals.current ?? null,
                  pwm: vals.pwm ?? null,
                  temp: vals.temp ?? null,
                  errors: errs,
                  warnings: warns,
                  trendDir: tDir,
                  trendArrow: tDir === 'up' ? '↑' : tDir === 'down' ? '↓' : '→',
                };
              });
              this.tooltipEvgCards.set(cards);

              // ── Smart tooltip positioning using fixed (viewport) coords ──
              // position: fixed bypasses all parent overflow:hidden clipping
              const canvasRect = chart.canvas.getBoundingClientRect();
              const GAP = 14;

              // Estimate tooltip dimensions from card count
              const cols  = Math.min(3, cards.length) || 1;
              const rows  = Math.ceil(cards.length / cols);
              const TIP_W = cols * 208 + 20;
              const TIP_H = 34 + rows * 72;

              // Cursor in viewport coordinates
              const cursorX = canvasRect.left + tooltip.caretX;
              const cursorY = canvasRect.top  + tooltip.caretY;
              const VW = window.innerWidth;
              const VH = window.innerHeight;

              // Horizontal: prefer right; flip left if near viewport edge
              let left = cursorX + GAP;
              if (left + TIP_W > VW - 8) left = cursorX - TIP_W - GAP;
              left = Math.max(8, Math.min(left, VW - TIP_W - 8));

              // Vertical: prefer above cursor; fall back to below if near top
              let top = cursorY - TIP_H - GAP;
              if (top < 8) top = cursorY + GAP;
              top = Math.max(8, Math.min(top, VH - TIP_H - 8));

              this.tooltipLeft.set(left);
              this.tooltipTop.set(top);
              this.tooltipWidth.set(TIP_W);
              this.tooltipVisible.set(true);
              chart.draw();
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            min: now - windowMs,
            max: now,
            title: { display: true, text: 'Time' },
            ticks: {
              maxTicksLimit: 6,
              maxRotation: 45,
              minRotation: 45,
              callback: (val: any) =>
                new Date(val).toLocaleTimeString('en-US', {
                  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                })
            }
          },
          y: { display: false }     // placeholder until datasets are added
        }
      }
    };

    this.chart = new Chart(ctx, config);
    this.updateChart();
  }

  /**
   * Advance the rolling time window every second (keeps axis scrolling even when offline)
   */
  private tickChart(): void {
    if (!this.chart?.options?.scales?.['x']) return;
    const now = Date.now();
    const windowMs = this.WINDOW_MS[this.liveTimeRange()] ?? 300_000;
    const xScale = this.chart.options.scales['x'] as any;
    xScale.min = now - windowMs;
    xScale.max = now;
    this.chart.update('none');
    this.updateStatsBar();
  }

  /**
   * Rebuild datasets when series selection or history changes
   */
  private updateChart(): void {
    if (!this.chart) return;

    const series = this.selectedSeries();
    const history = this.historicalData();
    const now = Date.now();
    const windowMs = this.WINDOW_MS[this.liveTimeRange()] ?? 300_000;

    // Compute error/warning annotations from history snapshots
    const newAnnotations: Array<{x: number; label: string; color: string}> = [];
    if (this.showErrorIndicators() || this.showWarningIndicators()) {
      history.forEach(h => {
        if (!h.devices?.length) return;
        const ts = (h.timestamp as Date).getTime();
        for (let i = 1; i <= 6; i++) {
          const evg = this.dashboardData.extractEVGData(h.devices[0], i);
          if (!evg) continue;
          const { errors, warnings } = analyzeEVGData(
            { ...evg, lamp1_status: evg.running, lamp2_status: evg.running }, i
          );
          if (this.showErrorIndicators()) {
            errors.filter(c => this.selectedErrorCodes().includes(c)).forEach(c => {
              newAnnotations.push({ x: ts, label: `E${c}·${i}`, color: ERROR_COLOR_MAP[c] ?? '#ef4444' });
            });
          }
          if (this.showWarningIndicators()) {
            warnings.filter(c => this.selectedWarningCodes().includes(c)).forEach(c => {
              newAnnotations.push({ x: ts, label: `W${c}·${i}`, color: WARNING_COLOR_MAP[c] ?? '#f59e0b' });
            });
          }
        }
      });
    }
    this.errorAnnotations = newAnnotations;

    if (series.length === 0) {
      this.errorAnnotations = [];
      this.chart.data.datasets = [];
      this.chart.update('none');
      return;
    }

    // Build {x: ms-timestamp, y: value} datasets — keep all history points
    const datasets = series.map(({ evgId, parameter }) => {
      const color = EVG_COLORS[evgId - 1];
      const cfg = PARAMETER_CONFIG[parameter];

      const data = history
        .filter(h => h.devices?.length)
        .map(h => {
          const evg = this.dashboardData.extractEVGData(h.devices[0], evgId);
          if (!evg) return null;
          const v = { voltage: evg.voltage, current: evg.current, pwm: evg.powerLevel, temp: evg.temperature }[parameter];
          return { x: (h.timestamp as Date).getTime(), y: v };
        })
        .filter((p): p is { x: number; y: number } => p !== null);

      return {
        label: `EVG ${evgId} - ${cfg.label} (${cfg.suffix})`,
        data,
        borderColor: color,
        backgroundColor: (context: any) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) return color + '10';
          const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          g.addColorStop(0, color + '30');
          g.addColorStop(1, color + '00');
          return g;
        },
        yAxisID: cfg.yAxisID,
        fill: true,
        ...CHART_STYLE
      };
    });

    // Build Y-axes
    const usedParams = new Set(series.map(s => s.parameter));
    const yAxes: any = {};
    if (usedParams.has('voltage')) {
      yAxes['y-voltage'] = { type: 'linear', display: true, position: 'left',
        title: { display: true, text: `(${PARAMETER_CONFIG.voltage.suffix})` },
        grid: { drawOnChartArea: true, color: PARAMETER_CONFIG.voltage.gridColor } };
    }
    if (usedParams.has('current')) {
      yAxes['y-current'] = { type: 'linear', display: true, position: 'right',
        title: { display: true, text: `(${PARAMETER_CONFIG.current.suffix})` },
        grid: { drawOnChartArea: false } };
    }
    if (usedParams.has('pwm')) {
      yAxes['y-pwm'] = { type: 'linear', display: true, position: 'left',
        title: { display: true, text: `(${PARAMETER_CONFIG.pwm.suffix})` },
        suggestedMin: 0,
        grid: { drawOnChartArea: true, color: PARAMETER_CONFIG.pwm.gridColor } };
    }
    if (usedParams.has('temp')) {
      yAxes['y-temp'] = { type: 'linear', display: true, position: 'right',
        title: { display: true, text: `(${PARAMETER_CONFIG.temp.suffix})` },
        grid: { drawOnChartArea: false } };
    }

    this.chart!.data.datasets = datasets as any[];
    this.chart!.options.scales = {
      x: {
        type: 'linear',
        min: now - windowMs,
        max: now,
        title: { display: true, text: 'Time' },
        ticks: {
          maxTicksLimit: 6,
          maxRotation: 45,
          minRotation: 45,
          callback: (val: any) =>
            new Date(val).toLocaleTimeString('en-US', {
              hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
            })
        }
      },
      ...yAxes
    };
    this.chart.update('none');
    this.updateStatsBar();
  }

  private updateStatsBar(): void {
    if (!this.chart?.data.datasets.length) { this.statsBar.set([]); return; }
    const scale = this.chart.scales['x'];
    const xMin = scale?.min ?? -Infinity;
    const xMax = scale?.max ?? Infinity;
    type Acc = { min: number; max: number; sum: number; count: number };
    const acc: Record<string, Acc> = {};
    this.chart.data.datasets.forEach((ds: any) => {
      const param = Object.keys(PARAMETER_CONFIG).find(
        k => k !== 'temperature' && (PARAMETER_CONFIG as any)[k].yAxisID === ds.yAxisID
      );
      if (!param) return;
      if (!acc[param]) acc[param] = { min: Infinity, max: -Infinity, sum: 0, count: 0 };
      const a = acc[param];
      (ds.data as {x: number; y: number}[]).forEach(pt => {
        if (pt.x < xMin || pt.x > xMax || pt.y == null) return;
        a.min = Math.min(a.min, pt.y); a.max = Math.max(a.max, pt.y);
        a.sum += pt.y; a.count++;
      });
    });
    this.statsBar.set(
      Object.entries(acc).filter(([, a]) => a.count > 0).map(([param, a]) => {
        const cfg = (PARAMETER_CONFIG as any)[param];
        return { param, label: cfg.label, suffix: cfg.suffix,
                 min: a.min.toFixed(1), max: a.max.toFixed(1), avg: (a.sum / a.count).toFixed(1) };
      })
    );
  }

  /**
   * Maps a dataset label segment (e.g. "Voltage (V)") to a parameter key
   */
  private getLabelToParam(labelSegment: string): 'voltage' | 'current' | 'pwm' | 'temp' | null {
    const s = labelSegment.toLowerCase();
    if (s.startsWith('voltage')) return 'voltage';
    if (s.startsWith('current')) return 'current';
    if (s.startsWith('pwm') || s.startsWith('power')) return 'pwm';
    if (s.startsWith('temp')) return 'temp';
    return null;
  }
}
